import { API_PREFIX } from '../../schema/mapSet'

export interface LineStatusEntry {
  /** TfL `statusSeverity` (10 = Good Service). */
  readonly severity: number
  /** TfL `statusSeverityDescription`, e.g. `Minor Delays`. */
  readonly description: string
  /** TfL `reason` — the disruption text ("Minor delays due to …"); absent on Good Service. */
  readonly reason?: string
}

interface LineStatusDto {
  readonly id?: string
  readonly lineStatuses?: ReadonlyArray<{
    readonly statusSeverity?: number
    readonly statusSeverityDescription?: string
    readonly reason?: string
  }>
}

interface Envelope {
  readonly data?: readonly LineStatusDto[]
  readonly success?: boolean
}

/**
 * Thin client for `GET /api/v2/line-status?ids=<line,ids>` (city server
 * TfL passthrough; v2 line ids are TfL ids) feeding the "Other lines" rows. A line can carry
 * several simultaneous statuses (e.g. Good Service + Planned Closure
 * for the weekend) — the WORST (lowest severity) wins, mirroring how
 * TfL's own board surfaces it.
 *
 * Fail-safe (Safe-client rule 2): any failure yields `{}` — rows fall
 * back to Good service, the page never throws.
 */
export async function fetchLineStatuses(
  tflLineIds: readonly string[],
): Promise<Record<string, LineStatusEntry>> {
  if (tflLineIds.length === 0) return {}
  try {
    // detail=true: the minimal shape is descriptions-only with no line
    // ids; only the detailed one carries id + statusSeverity. Payload is
    // wrapped in metro-server's { data, success } base envelope.
    const res = await fetch(
      `${API_PREFIX}/line-status?ids=${encodeURIComponent(tflLineIds.join(','))}&detail=true`,
    )
    if (!res.ok) return {}
    const envelope = (await res.json()) as Envelope
    if (envelope.success !== true || envelope.data === undefined) return {}
    const out: Record<string, LineStatusEntry> = {}
    for (const line of envelope.data) {
      if (line.id === undefined) continue
      for (const status of line.lineStatuses ?? []) {
        if (status.statusSeverity === undefined) continue
        const current = out[line.id]
        if (current === undefined || status.statusSeverity < current.severity) {
          out[line.id] = {
            severity: status.statusSeverity,
            description: status.statusSeverityDescription ?? '',
            ...(status.reason !== undefined && status.reason !== ''
              ? { reason: status.reason }
              : {}),
          }
        }
      }
    }
    return out
  } catch {
    return {}
  }
}
